const HIGHLIGHT_SCROLL_SETTLE_MS = 150;

// AkTooltipInformation's own popper doesn't exist until `isEnabled`/`show` reactively becomes
// true - it computes its position once, right as it first appears, and never recomputes
// automatically, including on scroll. Once a sticky ancestor freezes the trigger's screen
// position, the tooltip - still anchored to the document coordinates it was first computed at -
// drifts away.
//
// Re-nudging on every scroll event mostly fixes that, but on fast scrolls the popper can briefly
// jump to the wrong spot: floating-vue positions via `documentY = triggerViewportY + scrollY`,
// which only holds if both are read at the same instant - a fast-changing scrollY breaks that.
// Rather than chasing a moving target, the tooltip is hidden for the scroll's duration and
// revealed, freshly repositioned, once scrolling settles.
export function useHighlightScrollTracking(popperSelector: string) {
  let scrollSettleId: ReturnType<typeof setTimeout> | undefined;

  const setHiddenForScroll = (hidden: boolean) => {
    document.querySelectorAll<HTMLElement>(popperSelector).forEach((popper) => {
      popper.style.opacity = hidden ? '0' : '';
      popper.style.pointerEvents = hidden ? 'none' : '';
    });
  };

  const handleScroll = () => {
    setHiddenForScroll(true);
    clearTimeout(scrollSettleId);
    scrollSettleId = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      setHiddenForScroll(false);
    }, HIGHLIGHT_SCROLL_SETTLE_MS);
  };

  const startTrackingScroll = () => {
    window.dispatchEvent(new Event('resize'));
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
  };

  const stopTrackingScroll = () => {
    window.removeEventListener('scroll', handleScroll, { capture: true });
    clearTimeout(scrollSettleId);
    scrollSettleId = undefined;
    setHiddenForScroll(false);
  };

  return { startTrackingScroll, stopTrackingScroll };
}
